import React, { useEffect } from "react";
import {
	Box,
	IconButton,
	InputAdornment,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { Field } from "@onair/minimal-ui/hook-form";
import { useFormContext, useWatch } from "react-hook-form";
import { RHFInputDecimalField } from "@onair/ui/form-controls/input-decimal";
import InputLabelWizard from "../input-label-wizard";
import { Iconify } from "@onair/minimal-ui/iconify";

interface PropTypes {
	prefixName: string;
	disableRemove?: boolean;
	onDelete: () => void;
}

const TicketTierBox: React.FC<PropTypes> = ({
	prefixName,
	disableRemove = false,
	onDelete,
}) => {
	const { control, setValue } = useFormContext();

	const isUnlimitedAudiences = useWatch({
		name: `${prefixName}.isUnlimitedAudiences`,
		control,
	});

	const isUnlimitedTicketPerUser = useWatch({
		name: `${prefixName}.isUnlimitedTicketPerUser`,
		control,
	});

	const isFree = useWatch({
		name: `${prefixName}.isFree`,
		control,
	});

	useEffect(() => {
		if (isUnlimitedTicketPerUser) {
			setValue(`${prefixName}.limitTicketPerUser`, undefined, {
				shouldValidate: true,
			});
		}

		if (isUnlimitedAudiences) {
			setValue(`${prefixName}.limitAudiences`, undefined, {
				shouldValidate: true,
			});
		}

		if (isFree) {
			setValue(`${prefixName}.farePrice`, undefined, {
				shouldValidate: true,
			});
		}
	}, [isUnlimitedAudiences, isUnlimitedTicketPerUser, isFree]);

	return (
		<Stack spacing={2}>
			<Stack spacing={1}>
				<Stack className="flex flex-row items-center justify-between">
					<InputLabelWizard required>Tên loại vé</InputLabelWizard>
					<Box>
						<IconButton
							disabled={disableRemove}
							onClick={onDelete}
							size="small"
						>
							<Iconify
								width={16}
								sx={{
									color: disableRemove ? "action.disabled" : "action.active",
								}}
								icon={`solar:trash-bin-trash-bold`}
							/>
						</IconButton>
					</Box>
				</Stack>
				<Field.Text name={`${prefixName}.name`} label="Tên loại vé" required />
			</Stack>

			<Stack spacing={1}>
				<Field.Switch
					name={`${prefixName}.isUnlimitedAudiences`}
					label={
						<Typography fontWeight={600} variant="body2">
							Không giới hạn sức chứa
						</Typography>
					}
				/>
				{isUnlimitedAudiences ? (
					<TextField
						label="Số người đăng ký"
						required
						value="Không giới hạn"
						disabled
					/>
				) : (
					<RHFInputDecimalField
						name={`${prefixName}.limitAudiences`}
						label="Số người đăng ký"
						required
					/>
				)}
			</Stack>

			<Stack spacing={1}>
				<Field.Switch
					name={`${prefixName}.isFree`}
					label={
						<Typography fontWeight={600} variant="body2">
							Vé miễn phí
						</Typography>
					}
				/>
				{isFree ? (
					<TextField
						label="Giá vé"
						required
						value="Miễn phí"
						disabled
						InputProps={{
							endAdornment: <InputAdornment position="end"> ₫</InputAdornment>,
						}}
					/>
				) : (
					<RHFInputDecimalField
						name={`${prefixName}.farePrice`}
						label="Giá vé"
						required
						InputProps={{
							endAdornment: <InputAdornment position="end"> ₫</InputAdornment>,
						}}
					/>
				)}
			</Stack>

			<Stack spacing={1}>
				<Field.Switch
					name={`${prefixName}.isUnlimitedTicketPerUser`}
					label={
						<Typography fontWeight={600} variant="body2">
							Không giới hạn số vé mua
						</Typography>
					}
				/>
				{isUnlimitedTicketPerUser ? (
					<TextField
						label="Số lượng vé mua tối đa/user"
						required
						value="Không giới hạn"
						disabled
					/>
				) : (
					<RHFInputDecimalField
						name={`${prefixName}.limitTicketPerUser`}
						label="Số lượng vé mua tối đa/user"
						required
					/>
				)}
			</Stack>

			<Stack spacing={1}>
				<InputLabelWizard>Mô tả hạng vé</InputLabelWizard>
				<Field.Editor
					placeholder="Mô tả hạng vé"
					name={`${prefixName}.description`}
				/>
			</Stack>
		</Stack>
	);
};

export default TicketTierBox;
